import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import {
    computeWillBeUnmanagedOnDate,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import {
    useGetBenefitCheckConfigQuery,
    useGetMemberDetailsQuery,
} from 'store/api/scopeAssessorApi';
import { type ReferralDetails, type TreatmentDetails } from 'store/api/treatmentApi';

type MemberDetailsSource = ReferralDetails | TreatmentDetails;

const isTreatmentDetails = (source: MemberDetailsSource): source is TreatmentDetails =>
    'memberDetails' in source;

export const getScopeAssessorMemberDetailsQueryArgs = (source?: MemberDetailsSource | null) => {
    if (!source) {
        return skipToken;
    }

    if (isTreatmentDetails(source)) {
        return {
            payerId: source.payerId,
            segmentId: source.segmentId,
            memberId: source.memberDetails.memberId,
            firstName: source.memberDetails.firstName,
            lastName: source.memberDetails.lastName,
            dateOfBirth: source.memberDetails.dateOfBirth,
        };
    }

    return {
        payerId: source.payerId,
        segmentId: source.segmentId,
        memberId: source.memberId,
        firstName: source.memberFirstName,
        lastName: source.memberLastName,
        dateOfBirth: source.memberDOB,
    };
};

export const useScopeAssessorMemberDetails = (
    source?: MemberDetailsSource | null,
    { computeWillBeUnmanagedOn = false } = {},
) => {
    const { payersEnhancedEligibility } = useFlags<{ payersEnhancedEligibility: boolean }>();
    const memberQueryArgs = useMemo(() => getScopeAssessorMemberDetailsQueryArgs(source), [source]);

    const {
        data: memberDetails,
        isFetching,
        isError,
        refetch,
    } = useGetMemberDetailsQuery(memberQueryArgs);

    const shouldFetchBenefitCheckConfig =
        computeWillBeUnmanagedOn &&
        payersEnhancedEligibility &&
        memberQueryArgs !== skipToken &&
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;

    const { data: delayedUnmanagementDays } = useGetBenefitCheckConfigQuery(
        shouldFetchBenefitCheckConfig
            ? {
                  payerId: memberQueryArgs.payerId,
                  segmentId: memberQueryArgs.segmentId,
              }
            : skipToken,
    );

    const willBeUnmanagedOn = useMemo(() => {
        if (!shouldFetchBenefitCheckConfig) {
            return memberDetails?.willBeUnmanagedOn;
        }

        return (
            computeWillBeUnmanagedOnDate(memberDetails?.pendingAt, delayedUnmanagementDays) ??
            memberDetails?.willBeUnmanagedOn
        );
    }, [
        delayedUnmanagementDays,
        memberDetails?.pendingAt,
        memberDetails?.willBeUnmanagedOn,
        shouldFetchBenefitCheckConfig,
    ]);

    return {
        memberDetails,
        isFetching,
        isError,
        refetch,
        willBeUnmanagedOn,
    };
};
